import classNames from "classnames"
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form"
import { FormValues } from "."
import { ACTION_DESCRIPTIONS } from "@/constants/permissions-config"
import { UserRole } from "@/constants/enum"

interface PermissionCheckboxGroupProps {
    module: any
    roleIndex: number
    moduleIndex: number
    control: Control<FormValues, any>
    roleId: number
}

export default function PermissionCheckboxGroup({
    module,
    roleIndex,
    moduleIndex,
    control,
    roleId
}: PermissionCheckboxGroupProps) {
    // keyName defaults to "id", which would shadow each permission's numeric
    // casbin_rule id with react-hook-form's generated GUID. update() below
    // writes whole field objects back into form state, so that GUID would reach
    // the PUT body and fail the backend's int64 binding.
    const { fields, update } = useFieldArray({
        control,
        name: `roles.${roleIndex}.modules.${moduleIndex}.permissions`,
        keyName: '_key',
    })

    // Watch all permissions to handle dependencies
    const watchedPermissions = useWatch({
        control,
        name: `roles.${roleIndex}.modules.${moduleIndex}.permissions`,
    })

    // Separate permissions into App and API groups
    const appPermissions = fields.filter((_, index) => {
        const field = fields[index] as any
        return field.group === 'app'
    })

    const apiPermissions = fields.filter((_, index) => {
        const field = fields[index] as any
        return field.group === 'api'
    })

    // Handle permission dependencies: if view is unchecked, uncheck add, edit, and delete
    const handlePermissionChange = (permIndex: number, newValue: boolean, action: string) => {
        const perm = fields[permIndex] as any

        // If unchecking "view", also uncheck "add", "edit", and "delete"
        if (action === 'view' && !newValue && perm.group === 'app') {
            fields.forEach((field, idx) => {
                const f = field as any
                if (f.group === 'app' && (f.action === 'add' || f.action === 'edit' || f.action === 'delete')) {
                    const { _key, ...value } = f
                    update(idx, { ...value, value: false })
                }
            })
        }

        // If checking "add", "edit", or "delete", ensure "view" is also checked
        if ((action === 'add' || action === 'edit' || action === 'delete') && newValue && perm.group === 'app') {
            const viewIndex = fields.findIndex((f: any) => f.group === 'app' && f.action === 'view')
            if (viewIndex !== -1) {
                const viewField = fields[viewIndex] as any
                if (!viewField.value) {
                    const { _key, ...value } = viewField
                    update(viewIndex, { ...value, value: true })
                }
            }
        }
    }

    return (
        <div className="border-b border-stroke dark:border-strokedark pb-5">
            {/* Module Header */}
            <div className="mb-3">
                <h5 className="font-medium text-black dark:text-white uppercase text-sm">
                    {module.displayName}
                </h5>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {module.description}
                </p>
            </div>

            <div className="space-y-4">
                {/* App Permissions Group */}
                {appPermissions.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fa-light fa-browser text-primary text-sm"></i>
                            <span className="text-xs font-semibold text-primary uppercase">App Permissions</span>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 ml-5">
                            {fields.map((field, permIndex) => {
                                const perm = field as any
                                if (perm.group !== 'app') return null

                                // Hide add/edit/delete for View Only role
                                if (roleId === UserRole.VIEWONLY && (perm.action === 'add' || perm.action === 'edit' || perm.action === 'delete')) {
                                    return null
                                }

                                return (
                                    <div key={(field as any)._key} className="group relative">
                                        <label
                                            htmlFor={`perm-${roleIndex}-${moduleIndex}-${permIndex}`}
                                            className="flex cursor-pointer select-none items-center gap-2"
                                        >
                                            <Controller
                                                name={`roles.${roleIndex}.modules.${moduleIndex}.permissions.${permIndex}.value`}
                                                control={control}
                                                defaultValue={perm.value}
                                                render={({ field: { onChange, value, name, ref } }) => (
                                                    <div className="relative" ref={ref}>
                                                        <input
                                                            type="checkbox"
                                                            id={`perm-${roleIndex}-${moduleIndex}-${permIndex}`}
                                                            className="sr-only"
                                                            checked={value}
                                                            onChange={(e) => {
                                                                onChange(e.target.checked)
                                                                handlePermissionChange(permIndex, e.target.checked, perm.action)
                                                            }}
                                                        />
                                                        <div className={classNames(
                                                            "mr-2 flex h-5 w-5 items-center justify-center rounded border transition-colors",
                                                            {
                                                                "border-primary bg-gray-50 dark:bg-transparent": value,
                                                                "border-stroke dark:border-strokedark": !value
                                                            }
                                                        )}>
                                                            <span className={classNames(
                                                                "h-2.5 w-2.5 rounded-sm transition-colors",
                                                                { "bg-primary": value }
                                                            )}></span>
                                                        </div>
                                                    </div>
                                                )}
                                            />
                                            <span className="capitalize text-sm">{perm.action}</span>
                                        </label>

                                        {/* Tooltip */}
                                        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                            <div className="rounded bg-black px-3 py-2 text-xs text-white whitespace-nowrap shadow-lg">
                                                {ACTION_DESCRIPTIONS[perm.action as keyof typeof ACTION_DESCRIPTIONS]}
                                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* API Permissions Group */}
                {apiPermissions.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <i className="fa-brands fa-discord text-success text-sm"></i>
                            <span className="text-xs font-semibold text-success uppercase">API Permissions (Discord Bot)</span>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 ml-5">
                            {fields.map((field, permIndex) => {
                                const perm = field as any
                                if (perm.group !== 'api') return null

                                // Hide add/edit/delete/write for View Only role
                                if (roleId === UserRole.VIEWONLY && (perm.action === 'add' || perm.action === 'edit' || perm.action === 'delete' || perm.action === 'write')) {
                                    return null
                                }

                                return (
                                    <div key={(field as any)._key} className="group relative">
                                        <label
                                            htmlFor={`perm-${roleIndex}-${moduleIndex}-${permIndex}`}
                                            className="flex cursor-pointer select-none items-center gap-2"
                                        >
                                            <Controller
                                                name={`roles.${roleIndex}.modules.${moduleIndex}.permissions.${permIndex}.value`}
                                                control={control}
                                                defaultValue={perm.value}
                                                render={({ field: { onChange, value, name, ref } }) => (
                                                    <div className="relative" ref={ref}>
                                                        <input
                                                            type="checkbox"
                                                            id={`perm-${roleIndex}-${moduleIndex}-${permIndex}`}
                                                            className="sr-only"
                                                            checked={value}
                                                            onChange={(e) => {
                                                                onChange(e.target.checked)
                                                                handlePermissionChange(permIndex, e.target.checked, perm.action)
                                                            }}
                                                        />
                                                        <div className={classNames(
                                                            "mr-2 flex h-5 w-5 items-center justify-center rounded border transition-colors",
                                                            {
                                                                "border-success bg-gray-50 dark:bg-transparent": value,
                                                                "border-stroke dark:border-strokedark": !value
                                                            }
                                                        )}>
                                                            <span className={classNames(
                                                                "h-2.5 w-2.5 rounded-sm transition-colors",
                                                                { "bg-success": value }
                                                            )}></span>
                                                        </div>
                                                    </div>
                                                )}
                                            />
                                            <span className="capitalize text-sm">{perm.action}</span>
                                        </label>

                                        {/* Tooltip */}
                                        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                            <div className="rounded bg-black px-3 py-2 text-xs text-white whitespace-nowrap shadow-lg">
                                                {ACTION_DESCRIPTIONS[perm.action as keyof typeof ACTION_DESCRIPTIONS]}
                                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
